import { createElement } from "react";
/**
 * WordPress dependencies
 */
import { Button, privateApis as componentsPrivateApis } from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import { memo } from '@wordpress/element';
import { settings } from '@wordpress/icons';

/**
 * Internal dependencies
 */
import { unlock } from './lock-unlock';
import { SORTING_DIRECTIONS } from './constants';
import { VIEW_LAYOUTS } from './layouts';
const {
  DropdownMenuV2: DropdownMenu,
  DropdownMenuGroupV2: DropdownMenuGroup,
  DropdownMenuItemV2: DropdownMenuItem,
  DropdownMenuRadioItemV2: DropdownMenuRadioItem,
  DropdownMenuCheckboxItemV2: DropdownMenuCheckboxItem,
  DropdownMenuItemLabelV2: DropdownMenuItemLabel
} = unlock(componentsPrivateApis);
function ViewTypeMenu({
  view,
  onChangeView,
  supportedLayouts
}) {
  let _availableViews = VIEW_LAYOUTS;
  if (supportedLayouts) {
    _availableViews = _availableViews.filter(_view => supportedLayouts.includes(_view.type));
  }
  if (_availableViews.length === 1) {
    return null;
  }
  const activeView = _availableViews.find(v => view.type === v.type);
  return createElement(DropdownMenu, {
    trigger: createElement(DropdownMenuItem, {
      suffix: createElement("span", {
        "aria-hidden": "true"
      }, activeView.label)
    }, createElement(DropdownMenuItemLabel, null, __('Layout')))
  }, _availableViews.map(availableView => {
    return createElement(DropdownMenuRadioItem, {
      key: availableView.type,
      value: availableView.type,
      name: "view-actions-available-view",
      checked: availableView.type === view.type,
      hideOnClick: true,
      onChange: e => {
        onChangeView({
          ...view,
          type: e.target.value
        });
      }
    }, createElement(DropdownMenuItemLabel, null, availableView.label));
  }));
}
const PAGE_SIZE_VALUES = [10, 20, 50, 100];
function PageSizeMenu({
  view,
  onChangeView
}) {
  return createElement(DropdownMenu, {
    trigger: createElement(DropdownMenuItem, {
      suffix: createElement("span", {
        "aria-hidden": "true"
      }, view.perPage)
    }, createElement(DropdownMenuItemLabel, null, __('Items per page')))
  }, PAGE_SIZE_VALUES.map(size => {
    return createElement(DropdownMenuRadioItem, {
      key: size,
      value: size,
      name: "view-actions-page-size",
      checked: view.perPage === size,
      onChange: () => {
        onChangeView({
          ...view,
          // `e.target.value` holds the same value as `size` but as a string,
          // so we use `size` directly to avoid parsing to int.
          perPage: size,
          page: 1
        });
      }
    }, createElement(DropdownMenuItemLabel, null, size));
  }));
}
function FieldsVisibilityMenu({
  view,
  onChangeView,
  fields
}) {
  const hidableFields = fields.filter(field => field.enableHiding !== false && field.id !== view.layout.mediaField);
  if (!hidableFields?.length) {
    return null;
  }
  return createElement(DropdownMenu, {
    trigger: createElement(DropdownMenuItem, null, createElement(DropdownMenuItemLabel, null, __('Fields')))
  }, hidableFields?.map(field => {
    return createElement(DropdownMenuCheckboxItem, {
      key: field.id,
      value: field.id,
      checked: !view.hiddenFields?.includes(field.id),
      onChange: () => {
        onChangeView({
          ...view,
          hiddenFields: view.hiddenFields?.includes(field.id) ? view.hiddenFields.filter(id => id !== field.id) : [...(view.hiddenFields || []), field.id]
        });
      }
    }, createElement(DropdownMenuItemLabel, null, field.header));
  }));
}
function SortMenu({
  fields,
  view,
  onChangeView
}) {
  const sortableFields = fields.filter(field => field.enableSorting !== false);
  if (!sortableFields?.length) {
    return null;
  }
  const currentSortedField = fields.find(field => field.id === view.sort?.field);
  return createElement(DropdownMenu, {
    trigger: createElement(DropdownMenuItem, {
      suffix: createElement("span", {
        "aria-hidden": "true"
      }, currentSortedField?.header)
    }, createElement(DropdownMenuItemLabel, null, __('Sort by')))
  }, sortableFields?.map(field => {
    const sortedDirection = view.sort?.direction;
    return createElement(DropdownMenu, {
      key: field.id,
      trigger: createElement(DropdownMenuItem, null, createElement(DropdownMenuItemLabel, null, field.header)),
      style: {
        minWidth: '220px'
      }
    }, Object.entries(SORTING_DIRECTIONS).map(([direction, info]) => {
      const isChecked = currentSortedField !== undefined && sortedDirection === direction && field.id === currentSortedField.id;
      const value = `${field.id}-${direction}`;
      return createElement(DropdownMenuRadioItem, {
        key: value
        // All sorting radio items share the same name, so that
        // selecting a sorting option automatically deselects the
        // previously selected one, even if it is displayed in
        // another submenu. The field and direction are passed via
        // the `value` prop.
        ,
        name: "view-actions-sorting",
        value: value,
        checked: isChecked,
        onChange: () => {
          onChangeView({
            ...view,
            sort: {
              field: field.id,
              direction
            }
          });
        }
      }, createElement(DropdownMenuItemLabel, null, info.label));
    }));
  }));
}
const ViewActions = memo(function ViewActions({
  fields,
  view,
  onChangeView,
  supportedLayouts
}) {
  return createElement(DropdownMenu, {
    trigger: createElement(Button, {
      size: "compact",
      icon: settings,
      label: __('View options')
    })
  }, createElement(DropdownMenuGroup, null, createElement(ViewTypeMenu, {
    view: view,
    onChangeView: onChangeView,
    supportedLayouts: supportedLayouts
  }), createElement(SortMenu, {
    fields: fields,
    view: view,
    onChangeView: onChangeView
  }), createElement(FieldsVisibilityMenu, {
    fields: fields,
    view: view,
    onChangeView: onChangeView
  }), createElement(PageSizeMenu, {
    view: view,
    onChangeView: onChangeView
  })));
});
export default ViewActions;
//# sourceMappingURL=view-actions.js.map